"use client";

import React, { useEffect, useMemo, useRef, useState } from "react";

import Image from "next/image";
import Chip from "@/components/global/ui/Chip";

import { cn } from "@/lib/utils";
import { PortableText } from "@portabletext/react";
import { richTextComponent } from "@/sanity/sanityPortableText";
import { HLSVideoPlayer } from "@/components/ui/SanityVideoPlayer";
import DecorationLine from "@/components/global/ui/DecorationLine";
import { CTACornerButton } from "@/shadcn/ui/corner-button";

import { PageAdLongSchema } from "@/sanity/pages/PageAdLongTemplate";

import dots from "@/public/vsl-short/dots.svg";
import dotsM from "@/public/vsl-short/dots-m.svg";

// how much of the video loops as a muted preview before the user clicks play
const PREVIEW_LOOP_SECONDS = 10;

export default function HeroSection({
  data,
  className,
}: {
  data: PageAdLongSchema;
  className?: string;
}) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isMuted, setIsMuted] = useState(true);
  const [started, setStarted] = useState(false);

  // Stable reference so a Sanity live-refresh (new object, same playbackId)
  // doesn't re-attach the HLS stream and reset the playhead to 0:00.
  const heroVideoSource = useMemo(
    () => data?.heroVideo,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [data?.heroVideo?.muxVideo?.playbackId, data?.heroVideo?.embedUrl],
  );

  const hasVideo =
    heroVideoSource?.muxVideo?.playbackId || heroVideoSource?.embedUrl;

  useEffect(() => {
    // keep the inline preview muted so autoplay is allowed
    if (videoRef.current) videoRef.current.muted = true;
  }, []);

  // before the user clicks play, loop only the first seconds as a teaser
  useEffect(() => {
    const video = videoRef.current;
    if (!video || started) return;

    const loopPreview = () => {
      if (video.currentTime >= PREVIEW_LOOP_SECONDS) video.currentTime = 0;
    };
    video.addEventListener("timeupdate", loopPreview);
    return () => video.removeEventListener("timeupdate", loopPreview);
  }, [started]);

  // play from the start with audio + native controls when the user clicks
  const handleStart = () => {
    const video = videoRef.current;
    if (!video) return;
    video.currentTime = 0;
    video.muted = false;
    video.play();
    setIsMuted(false);
    setStarted(true);
  };

  const playButton = !started && (
    <button
      onClick={handleStart}
      className="absolute top-1/2 left-1/2 rounded-full border -translate-x-1/2 -translate-y-1/2 hover:cursor-pointer border-white/20 p-[0.5rem]"
      aria-label={data?.heroPlayLabel || "Video ansehen"}
    >
      <span className="flex justify-center items-center rounded-full h-[7rem] gap-[1rem] bg-[#D9F9B9] px-[3rem] py-[1rem]">
        <svg
          className="h-[2rem] w-[2rem]"
          viewBox="0 0 16 16"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path d="M4 2.5v11l9-5.5-9-5.5z" fill="rgba(0,0,0,0.8)" />
        </svg>
        <span className="font-medium leading-none whitespace-nowrap font-geist text-[2.25rem] text-black/80">
          {data?.heroPlayLabel || "Video ansehen"}
        </span>
      </span>
    </button>
  );

  return (
    <section className={cn("relative", className)}>
      <div className="relative flex flex-col items-center px-[2rem] pt-[6rem] pb-[6rem] sm:px-[3rem] md:px-[5rem] lg:pt-[8.75rem] lg:pb-[6.5rem]">
        {/* horizontal divider under the header */}
        <DecorationLine
          className="top-0"
          orientation="horizontal"
          style="dark"
        />

        {/* vertical side lines flanking the content (near edges on mobile, matching Figma inset on desktop) */}
        <DecorationLine
          className="top-0 left-[1rem] lg:left-[8.85%]"
          orientation="vertical"
          style="dark"
        />
        <DecorationLine
          className="top-0 right-[1rem] lg:right-[8.85%]"
          orientation="vertical"
          style="dark"
        />

        <Image
          src={dots}
          alt=""
          className="hidden lg:block absolute top-0 left-1/2 -translate-x-1/2 !w-full lg:!w-[119.875rem] z-0 pointer-events-none"
        />
        <Image
          src={dotsM}
          alt=""
          className="lg:hidden absolute top-0 left-1/2 -translate-x-1/2 !w-full lg:!w-[119.875rem] z-0 pointer-events-none"
        />

        {/* eyebrow chip */}
        {data?.heroChip?.text && (
          <Chip
            className="mb-[2rem] [&_p]:uppercase [&_p]:font-geistmono [&_p]:text-[#202223] relative z-[1]"
            image={data?.heroChip?.icon}
            text={data?.heroChip?.text}
          />
        )}

        {/* headline */}
        {data?.heroTitle && (
          <div className="pixel-highlight richtext richtext-center w-[min(113.75rem,100%)] relative z-[1] prose-em:text-[4rem] sm:prose-em:text-[5.5rem] lg:prose-em:text-[7rem] prose-em:font-ivy prose-em:!px-0 lg:prose-em:!px-[.7rem] prose-headings:font-funnel prose-headings:font-medium prose-headings:text-black prose-headings:text-[4.5rem] sm:prose-headings:text-[5.5rem] lg:prose-headings:text-[7rem] prose-headings:leading-[1.1] prose-headings:text-balance">
            <PortableText
              value={data.heroTitle}
              components={richTextComponent}
            />
          </div>
        )}

        {/* subtext */}
        {data?.heroText && (
          <div className="richtext richtext-center mt-[1.5rem] w-[min(63rem,100%)] relative z-[1] prose-p:text-black/60 prose-p:text-[2.25rem] lg:prose-p:text-[2.25rem] prose-p:leading-[1.5]">
            <PortableText
              value={data.heroText}
              components={richTextComponent}
            />
          </div>
        )}

        {/* video — autoplays muted inline; click restarts from 0 with audio */}
        {hasVideo ? (
          <div className="relative z-[1] mt-[4.25rem] w-[min(133rem,100%)] aspect-video overflow-clip [clip-path:polygon(0_0,calc(100%-4rem)_0,100%_4rem,100%_100%,0_100%)]">
            <HLSVideoPlayer
              ref={videoRef}
              source={heroVideoSource}
              autoPlay={!started}
              muted={isMuted}
              loop={!started}
              playsInline
              controls={started}
              poster={data?.heroVideoPlaceholder?.src}
              className="absolute left-0 top-0 !h-full !w-full object-cover"
            />
            {playButton}
          </div>
        ) : (
          data?.heroVideoPlaceholder?.src && (
            <div className="relative z-[1] mt-[4.25rem] w-[min(133rem,100%)] aspect-video overflow-clip [clip-path:polygon(0_0,calc(100%-4rem)_0,100%_4rem,100%_100%,0_100%)]">
              <Image
                className="absolute left-0 top-0 !h-full !w-full object-cover"
                src={data.heroVideoPlaceholder}
                alt=""
              />
              {playButton}
            </div>
          )
        )}

        {/* ctas */}
        {(data?.heroCTA || data?.heroCTASecondary) && (
          <div className="relative z-[1] mt-[4rem] flex w-full flex-col items-center gap-[2rem] sm:w-auto sm:flex-row">
            {data?.heroCTA && <CTACornerButton data={data.heroCTA} />}
            {data?.heroCTASecondary && (
              <CTACornerButton data={data.heroCTASecondary} />
            )}
          </div>
        )}
      </div>

      {/* stats strip — full-bleed band framed by the same hairlines as the section */}
      {data?.heroStats?.length > 0 && (
        <div className="relative w-full border-y border-t-[#202223]/[.15] border-b-white max-sm:pb-[4rem]">
          <DecorationLine
            className="top-0 left-[1rem] lg:left-[8.85%] lg:hidden"
            orientation="vertical"
            style="dark"
          />
          <DecorationLine
            className="top-0 right-[1rem] lg:right-[8.85%] lg:hidden"
            orientation="vertical"
            style="dark"
          />
          <div className="relative mx-auto grid w-full grid-cols-1 lg:w-[82.3%] lg:grid-cols-4">
            {data.heroStats.map((stat, index) => (
              <div
                key={index}
                /* mobile stacks one stat per row: icon on the left, text beside it */
                className="relative flex flex-row items-center gap-[2.4rem] pl-[8rem] pr-[4rem] pt-[2rem] pb-[3rem] lg:flex-col lg:gap-[3rem] lg:px-[2rem] lg:py-[5.3125rem]"
              >
                {/* desktop cells are divided by a hairline down their left edge */}
                <DecorationLine
                  className="top-0 left-0 hidden lg:block"
                  orientation="vertical"
                  style="dark"
                />
                {/* stacked rows are divided horizontally instead — the band's own
                    top border already closes off the first row */}
                {index > 0 && (
                  <DecorationLine
                    className="top-0 left-0 lg:hidden"
                    orientation="horizontal"
                    style="dark"
                  />
                )}
                {index === data.heroStats.length - 1 && (
                  <DecorationLine
                    className="bottom-0 left-0 lg:hidden"
                    orientation="horizontal"
                    style="dark"
                  />
                )}
                {stat?.icon?.src && (
                  <div className="relative h-[7rem] w-[7rem] shrink-0 opacity-10">
                    <Image
                      src={stat.icon}
                      alt=""
                      className="!h-full !w-full object-contain"
                    />
                  </div>
                )}
                <div className="flex min-w-0 flex-1 flex-col items-start gap-[1rem] text-left lg:flex-initial lg:items-center lg:text-center">
                  <p className="m-0 font-ivy font-light italic text-[4rem] leading-none whitespace-nowrap text-black lg:text-[5rem]">
                    {stat.value}
                  </p>
                  <p className="m-0 font-geist text-[2rem] leading-[1.5] text-black/50 lg:text-[2.25rem] lg:text-black/90">
                    {stat.label}
                  </p>
                </div>
              </div>
            ))}

            {/* closing hairline on the right edge of the last cell (desktop grid only) */}
            <DecorationLine
              className="top-0 right-0 hidden lg:block"
              orientation="vertical"
              style="dark"
            />
          </div>
        </div>
      )}
    </section>
  );
}
